import {
  Autocomplete,
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';

import { useTranslation } from '../../../../utils/getLocalString';

// Original class: customPopper
const StyledCustomPopper = styled(Autocomplete)(theme => ({
  '& + .MuiAutocomplete-popper': {
    zIndex: 1,
    position: 'static!important',
    transform: 'none!important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));
// Original class: fieldsListItem
const StyledFieldsListItem = styled(Box)(({ theme }) => ({
  height: 36,
  padding: theme.spacing(2, 2),
  cursor: 'pointer',
  userSelect: 'none',

  '&:hover': {
    backgroundColor: '#F1F2F8',
  },

  '& .MuiAutocomplete-option': {
    '&.Mui-focused': {
      backgroundColor: 'red',
    },
  },
}));
// Original class: optionsFilterInput
const StyledOptionsFilterInput = styled(TextField)(({ theme }) => ({
  '&.MuiTextField-root': {
    '& .MuiInputBase-root': {
      padding: 0,
      borderRadius: theme.spacing(4, 4, 0, 0),
      backgroundColor: '#ffffff !important',
      borderBottom: '1px solid #E4E4EF',
      '& input': {
        padding: theme.spacing(2, 2),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 0,
      },
      '&:not(.Mui-error):hover': {
        borderBottom: '1px solid #E4E4EF',
      },
    },
  },
}));
// Original class: fieldsList
const StyledFieldsList = styled(Paper)(({ theme }) => ({
  '&.MuiPaper-root': {
    boxShadow: 'none',
    overflow: 'auto',
    borderRadius: theme.spacing(0, 0, 4, 4),
    maxHeight: '500px',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiAutocomplete-option': {
      padding: 0,
    },
    '& .MuiAutocomplete-listbox': {
      padding: 0,
      //height: 324,
      maxHeight: 'none',
    },
  },
}));

const DragDropListsNew = ({
  fields,
  getOptionLabel,
  nonDraggableIds = [],
  componentParent = 'default',
  handleChange,
}) => {
  const getLocalString = useTranslation();
  const [fieldsOptions, setFieldsOptions] = useState({
    availableFields: [],
    selectedFields: [],
  });

  useEffect(() => {
    if (fields) {
      setFieldsOptions(fields);
    }
  }, [fields]);

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return;
      }
      const { source, destination } = result;
      let fields = {};
      if (nonDraggableIds.length > 0) {
        if (
          destination.droppableId === 'selectedFields' &&
          (destination.index === 0 || destination.index === 1)
        ) {
          return;
        }
      }
      if (source.droppableId !== destination.droppableId) {
        const sourceItems = fieldsOptions[source.droppableId];
        const destinationItems = fieldsOptions[destination.droppableId];
        const currentElement = sourceItems[source.index];
        sourceItems.splice(source.index, 1);
        if (destination.index < 0) {
          destinationItems.push(currentElement);
        } else {
          destinationItems.splice(destination.index, 0, currentElement);
        }
        fields = {
          [source.droppableId]: sourceItems.map((item, index) => ({
            ...item,
            position: index,
          })),
          [destination.droppableId]: destinationItems.map((item, index) => ({
            ...item,
            position: index,
          })),
        };
      } else {
        const newItems = fieldsOptions[destination.droppableId];
        const currentElement = newItems[source.index];
        newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, currentElement);
        fields = {
          ...fieldsOptions,
          [destination.droppableId]: newItems.map((item, index) => ({
            ...item,
            position: index,
          })),
        };
      }
      handleChange(fields);
    },
    [fieldsOptions, handleChange, nonDraggableIds.length],
  );
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: option => option.fieldName,
  });
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid
        container
        sx={{
          marginTop: '20px',
          overflow: 'hidden',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            marginRight: '10px',
            width: '50%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant='h5'
              sx={{
                marginLeft: 3,
              }}
            >
              {componentParent === 'agile'
                ? getLocalString('labels', 'avalibleFields')
                : getLocalString('labels', 'avalibleColumns')}
            </Typography>
            <StyledCustomPopper
              filterOptions={filterOptions}
              open
              multiple
              disablePortal
              disableClearable
              options={fieldsOptions.availableFields}
              getOptionLabel={getOptionLabel}
              renderOption={params => {
                const paramName = params.key.split(';').reverse()[0];
                const paramId = params.key.split(';').reverse()[1];
                let index = fieldsOptions.availableFields.indexOf(
                  fieldsOptions.availableFields.find(
                    item => item.fieldId === paramId,
                  ),
                );
                const isBlocked = nonDraggableIds.includes(paramId);
                return (
                  <Draggable
                    key={paramId}
                    draggableId={paramId}
                    index={index}
                    isDragDisabled={isBlocked}
                  >
                    {provided => {
                      return (
                        <StyledFieldsListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={() => {
                            if (!isBlocked) {
                              onDragEnd({
                                destination: {
                                  droppableId: 'selectedFields',
                                  index: -1,
                                },
                                source: {
                                  droppableId: 'availableFields',
                                  index: index,
                                },
                              });
                            }
                          }}
                        >
                          <Grid key={params['data-option-index']}>
                            <Typography
                              variant='h5'
                              sx={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {paramName}
                            </Typography>
                          </Grid>
                        </StyledFieldsListItem>
                      );
                    }}
                  </Draggable>
                );
              }}
              renderInput={params => {
                return (
                  <Box
                    key={params.value}
                    onMouseDown={params.inputProps.onMouseDown}
                  >
                    <StyledOptionsFilterInput
                      variant='outlined'
                      {...params}
                      placeholder={getLocalString('labels', 'input')}
                    />
                  </Box>
                );
              }}
              // renderTags={props.renderFirstStepTags}
              noOptionsText={
                <Typography variant='h5'>
                  {getLocalString('labels', 'notFound')}
                </Typography>
              }
              slots={{
                paper: params => {
                  return (
                    <Droppable droppableId='availableFields'>
                      {provided => {
                        return (
                          <>
                            {provided.placeholder}
                            <StyledFieldsList>
                              <Grid
                                container
                                direction='column'
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                              >
                                {params.children}
                              </Grid>
                            </StyledFieldsList>
                          </>
                        );
                      }}
                    </Droppable>
                  );
                },
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            overflow: 'hidden',
            marginLeft: '10px',
            width: '50%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant='h5'
              sx={{
                marginLeft: 3,
              }}
            >
              {componentParent === 'agile'
                ? getLocalString('labels', 'selectedFields')
                : getLocalString('labels', 'selectedColumns')}
            </Typography>
            <StyledCustomPopper
              filterOptions={filterOptions}
              multiple
              disableClearable
              disablePortal
              open
              options={fieldsOptions.selectedFields}
              getOptionLabel={getOptionLabel}
              renderOption={params => {
                const paramName = params.key.split(';').reverse()[0];
                const paramId = params.key.split(';').reverse()[1];
                const isBlocked = nonDraggableIds.includes(paramId);
                let index = fieldsOptions.selectedFields.indexOf(
                  fieldsOptions.selectedFields.find(
                    item => item.fieldId === paramId,
                  ),
                );
                return (
                  <Draggable
                    key={params.id}
                    draggableId={params.id}
                    index={index}
                    isDragDisabled={isBlocked}
                  >
                    {provided => {
                      return (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={() => {
                            if (
                              !isBlocked &&
                              !(paramId === 'resourcePlan.monthly_distribution')
                            ) {
                              onDragEnd({
                                destination: {
                                  droppableId: 'availableFields',
                                  index: -1,
                                },
                                source: {
                                  droppableId: 'selectedFields',
                                  index: index,
                                },
                              });
                            }
                          }}
                        >
                          <StyledFieldsListItem>
                            <Typography
                              variant='h5'
                              sx={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {paramName}
                            </Typography>
                          </StyledFieldsListItem>
                        </Box>
                      );
                    }}
                  </Draggable>
                );
              }}
              renderInput={params => {
                return (
                  <Box
                    key={params.value}
                    onMouseDown={params.inputProps.onMouseDown}
                  >
                    <StyledOptionsFilterInput
                      variant='outlined'
                      {...params}
                      placeholder={getLocalString('labels', 'input')}
                    />
                  </Box>
                );
              }}
              noOptionsText={
                <Typography variant='h5'>
                  {getLocalString('labels', 'notFound')}
                </Typography>
              }
              slots={{
                paper: params => {
                  return (
                    <Droppable droppableId='selectedFields'>
                      {provided => (
                        <>
                          {provided.placeholder}
                          <StyledFieldsList
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {params.children}
                          </StyledFieldsList>
                        </>
                      )}
                    </Droppable>
                  );
                },
              }}
            />
          </Box>
        </Box>
      </Grid>
    </DragDropContext>
  );
};

export default DragDropListsNew;
